import { type DefaultRole, PasswordRequester, type UserPreferences, type UserPreferencesToSave, UserRequester } from '@cobliteam/api-lib';

import type { UserData, UUID } from '@cobliteam/common-types';
import useSessionStore from '@cobliteam/hooks/src/useSession/store/useSessionStore';
import useUserDataStore from '@cobliteam/hooks/src/useSession/store/useUserDataStore';
import { Environment } from '@cobliteam/react-environments';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import invariant from 'tiny-invariant';
import { QUERY_KEY, QUERY_KEY_USER_DATA, QUERY_KEY_USER_PREFERENCES } from './queryKeys';
import useFleetHealth from './useFleetHealth';
import useUserData from './useUserData';
import useUserPermissions from './useUserPermissions';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getUserRequester = () => new UserRequester({ host: env.herbieApiHost });
const getPasswordRequester = () => new PasswordRequester({ host: env.herbieApiHost });

export { QUERY_KEY, QUERY_KEY_USER_DATA, QUERY_KEY_USER_PREFERENCES };

interface UserPreferencesCreateVariables {
  userId: UUID;
  preferencesToSave: UserPreferencesToSave;
}

interface UserPreferencesUpdateVariables {
  userId: UUID;
  preferencesToUpdate: UserPreferences;
}

interface UserPreferencesUpdateContext {
  previousPreferences?: UserPreferences;
  previousUserData?: UserData;
}

interface UserPermissionsUpdateVariables {
  userId: UUID;
  role: import('@cobliteam/common-types').UserRole;
  groupsRestriction: string[];
  customPermissions: string[];
}

interface PasswordUpdateVariables {
  email: string;
  oldPassword: string;
  newPassword: string;
}

/**
 * Compat facade. Recompõe a API antiga a partir dos hooks focados
 * (`useUserData`, `useFleetHealth`, `useUserPermissions`, `useSession`, `useCurrentUser`).
 * Novos consumidores devem usar os hooks focados diretamente; este continua exportado só
 * enquanto os ~20 call-sites existentes migram.
 */
const useUser = () => {
  const queryClient = useQueryClient();
  const session = useSessionStore((s) => s.session);
  const user = session?.user;

  const { hasRootGroupAccess, hasMaximumGroupsRestriction, hasGroupsRestriction, isSafetyCoacher, canEditAllGroups } = useUserPermissions();
  const { hasNoValidGroups, hasOnlyGroupsWithNoVehicles, noValidGroupOrVehicles } = useFleetHealth();

  const getSession = () => {
    invariant(session, 'Session is not defined');
    return session;
  };

  const getUser = () => {
    invariant(user, 'User is not defined');
    return user;
  };

  // Compat: assinatura antiga (userId, enabled). O userId é ignorado — a query é sempre keyed
  // no user logado. `useUserData` novo aceita apenas `enabled`.
  const useGetUserData = (_userId?: UUID, enabled = true) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useUserData(enabled);
  };

  const useDefaultRoles = (enabled = true) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<DefaultRole[]>({
      queryKey: [QUERY_KEY, 'DEFAULT_ROLES'],
      queryFn: () => getUserRequester().getDefaultRoles(),
      enabled,
      staleTime: Number.POSITIVE_INFINITY,
    });
  };

  const usePreferences = (userId?: UUID, enabled = true) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    const userIdLogged = enabled ? userId || getUser().id : '';

    return useQuery<UserPreferences | undefined>({
      queryKey: [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userIdLogged],
      queryFn: () => getUserRequester().getUserPreferences(userIdLogged),
      enabled,
    });
  };

  const updatePermissions = useMutation<void, unknown, UserPermissionsUpdateVariables>({
    mutationFn: ({ userId, role, groupsRestriction, customPermissions }) => getUserRequester().saveUserPermissions(userId, role, groupsRestriction, customPermissions),
  });

  const updatePassword = useMutation<void, unknown, PasswordUpdateVariables>({
    mutationFn: ({ email, oldPassword, newPassword }) => getPasswordRequester().changePassword(email, oldPassword, newPassword),
  });

  const createPreferences = useMutation<UUID, unknown, UserPreferencesCreateVariables>({
    mutationFn: (data) => getUserRequester().saveUserPreferences(data.userId, data.preferencesToSave),
    onMutate: async (data) => {
      const { userId } = data;

      queryClient.cancelQueries({
        queryKey: [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId],
      });

      const previousTodos = queryClient.getQueryData([QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId]);

      const createdUserPreferences: UserPreferences = {
        id: userId,
        vehicleGroupIds: data.preferencesToSave.vehicleGroupIds,
        useVehiclesCluster: data.preferencesToSave.useVehiclesCluster,
      };

      queryClient.setQueryData([QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId], createdUserPreferences);

      return { previousTodos };
    },
    onError: (_err, data, context: any) => {
      const { userId } = data;
      queryClient.setQueryData([QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId], context?.previousTodos);
      toast.error('Erro ao salvar preferências. Tente novamente mais tarde.');
    },
    onSettled: (_newTodo, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, QUERY_KEY_USER_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, variables.userId],
      });
    },
  });

  const updatePreferences = useMutation<void, unknown, UserPreferencesUpdateVariables, UserPreferencesUpdateContext>({
    mutationFn: (variables) => getUserRequester().updateUserPreferences(variables.userId, variables.preferencesToUpdate),
    onMutate: (variables) => {
      const { userId } = variables;

      const previousPreferences: UserPreferences | undefined = queryClient.getQueryData([QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId]);
      const previousUserData = queryClient.getQueryData<UserData>([QUERY_KEY, QUERY_KEY_USER_DATA]);

      // update userData no queryClient e no Zustand store — os dois têm que andar
      // juntos para que consumidores do store vejam as preferências novas na mesma sessão.
      if (previousUserData) {
        const updatedUserData: UserData = { ...previousUserData, userPreferences: variables.preferencesToUpdate };
        queryClient.setQueryData([QUERY_KEY, QUERY_KEY_USER_DATA], updatedUserData);
        useUserDataStore.getState().setUserData(userId, updatedUserData);
      }

      queryClient.cancelQueries({
        queryKey: [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId],
      });
      queryClient.setQueryData([QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId], variables.preferencesToUpdate);

      return { previousPreferences, previousUserData };
    },
    onError: (_, variables, context) => {
      if (context?.previousPreferences) {
        queryClient.setQueryData([QUERY_KEY, QUERY_KEY_USER_PREFERENCES, variables.userId], context.previousPreferences);
      } else {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, variables.userId],
        });
      }
      // Rollback do userData otimista — restaura queryClient e Zustand store para o estado anterior.
      if (context?.previousUserData) {
        queryClient.setQueryData([QUERY_KEY, QUERY_KEY_USER_DATA], context.previousUserData);
        useUserDataStore.getState().setUserData(variables.userId, context.previousUserData);
      }
      toast.error('Erro ao salvar preferências. Tente novamente mais tarde.');
    },
  });

  return {
    QUERY_KEY_USER_DATA,
    QUERY_KEY,
    user,
    hasRootGroupAccess,
    hasNoValidGroups,
    hasMaximumGroupsRestriction,
    hasGroupsRestriction,
    canEditAllGroups,
    noValidGroupOrVehicles,
    hasOnlyGroupsWithNoVehicles,
    session,
    getUser,
    useGetUserData,
    getSession,
    usePreferences,
    useDefaultRoles,
    updatePassword,
    createPreferences,
    updatePreferences,
    updatePermissions,
    isSafetyCoacher,
  };
};

export default useUser;
